// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Box geometry for the game info video.
 *
 * The box used to be a fixed aspect ratio taken from the game definition, which declared the same
 * 1280x720 / 300x530 for every game while the real videos ranged from 16:9 to 1:1 to 9:16. Anything
 * that was not exactly the declared shape got letterboxed against the placeholder background. Here
 * the box takes the video's own aspect ratio, so `CONTAIN` has nothing left to letterbox.
 */

export const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;

export type InfoVideoBox = {
  width: number;
  height: number;
};

/**
 * Largest box with the given aspect ratio that fits in `availableWidth` x `maxHeight`.
 *
 * `maxHeight` is what stops a 9:16 video from filling the modal and pushing the description off
 * screen; when it binds, the box gets narrower rather than being cropped or padded.
 */
export function resolveInfoVideoBox(
  aspectRatio: number,
  availableWidth: number,
  maxHeight: number
): InfoVideoBox {
  const safeAspect = Number.isFinite(aspectRatio) && aspectRatio > 0
    ? aspectRatio
    : DEFAULT_VIDEO_ASPECT_RATIO;
  const safeWidth = Number.isFinite(availableWidth) && availableWidth > 0 ? availableWidth : 0;
  const safeMaxHeight = Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : 0;

  if (safeWidth <= 0 || safeMaxHeight <= 0) {
    return { width: 0, height: 0 };
  }

  const width = Math.min(safeWidth, safeMaxHeight * safeAspect);
  return {
    width: Math.round(width),
    height: Math.round(width / safeAspect),
  };
}

/**
 * Aspect ratio declared by the game definition, used until the video reports its real size.
 *
 * Web and mobile are separate fields because the two delivered variants are fitted to different
 * bounds, so a 16:9 source is 1280x720 on web but 300x168 on mobile.
 */
export function resolveDeclaredAspectRatio(
  game: {
    videoWebWidth?: number | null;
    videoWebHeight?: number | null;
    videoMobileWidth?: number | null;
    videoMobileHeight?: number | null;
  } | null,
  preferMobile: boolean
): number {
  const width = preferMobile ? game?.videoMobileWidth : game?.videoWebWidth;
  const height = preferMobile ? game?.videoMobileHeight : game?.videoWebHeight;
  if (typeof width === 'number' && width > 0 && typeof height === 'number' && height > 0) {
    return width / height;
  }

  return DEFAULT_VIDEO_ASPECT_RATIO;
}

/**
 * Aspect ratio reported by the player once the video is ready.
 *
 * expo-av hands back `naturalSize` on native but forwards the raw DOM `canplay` event on web, where
 * the dimensions live on the `<video>` element itself. Returns null when neither is usable, so the
 * caller keeps the declared ratio.
 */
export function readVideoAspectRatio(event: unknown): number | null {
  const source = event as
    | { naturalSize?: { width?: number; height?: number }; target?: { videoWidth?: number; videoHeight?: number } }
    | null
    | undefined;
  const width = source?.naturalSize?.width ?? source?.target?.videoWidth;
  const height = source?.naturalSize?.height ?? source?.target?.videoHeight;
  if (typeof width === 'number' && width > 0 && typeof height === 'number' && height > 0) {
    return width / height;
  }

  return null;
}
